// Denied site data must not blank the page: this import goes first (job #768). See platform/safeStorage.ts.
import './platform/safeStorageBoot'
import { createApp } from 'vue'
import './style.css'
// Schools design system (CSS variables for schools dashboard components)
import './styles/design-tokens.css'
import './styles/global.css'
import App from './App.vue'
// Registers the lazy locale chunks with useI18n (job #966, see the file).
import './i18n/localeLoaders'
import router from './router'
import { shouldReloadForPreloadError, clearBootClasses } from './utils/bootHeal'
import { inNoReloadZone } from './platform/noReloadZone'
import { loadWebFonts } from './utils/loadWebFonts'
import { firstSound } from './playback/firstSoundSignal'
import { selectPrecacheEntriesToPoison } from './utils/wedgeCheat'
import { applyBootLocale } from './utils/deepLinkLocale'
import { isEmbedContext } from './platform/embedMode'
import { installApiOriginRewrite } from './platform/apiBase'
import { installShellSafeArea } from './platform/shellSafeArea'
import { captureOAuthReturnError } from './auth/googleSignIn'
import { rewriteLearnLink } from './platform/learnLink'
import { rewriteAffiliateLink, captureAffiliateTouch } from './platform/affiliateTouch'
import { captureCampaignArrival } from './platform/campaignArrival'
import { captureGoLanding, stripGoParams } from './composables/useGoLink'

// Point every app-relative `/api/...` request at the configured API origin.
// FIRST, before anything can make a request. On the web the configured origin
// is the empty string, so this installs nothing at all and every request is
// the identical relative path it has always been. Inside a native shell's
// WebView — whose own origin serves no API — it is the one place that makes
// the difference. See platform/apiBase.ts.
installApiOriginRewrite()

// A Google sign-in that FAILED comes back as `#error=...` in the fragment, and
// Supabase's own detectSessionInUrl strips that fragment the moment the client
// is built. Read it here, first, or the learner returns to an unchanged
// sign-in screen with no idea why nothing happened. See auth/googleSignIn.ts.
try { captureOAuthReturnError(window.location.hash) } catch { /* no window */ }

// Make the system-bar insets real for edge-anchored chrome, and make a
// MISSING measurement visible rather than silent. No-op on the web and in an
// iOS shell — it only switches on the Android native shell's clearance rule.
// See platform/shellSafeArea.ts and --shell-nav-clearance in design-tokens.css.
installShellSafeArea()

// A tab that arrived from a campaign link or another site: its tags are kept for this tab only
// and the arrival is counted, read from the real address BEFORE the /learn and /r rewrites below
// (so the landing path still names the course link) and before the router, whose picker-first
// redirect drops the query (job #702). Not in the framed demo, which lives on other people's pages.
// See platform/campaignArrival.ts for exactly what is kept, and where.
try { if (!isEmbedContext()) captureCampaignArrival() } catch { /* no window */ }

// A course link, /learn/<course_code>, is the short spelling of /?course=<code>.
// Rewritten here, before the locale inference below, the router and the app
// read the address, so every one of them sees the one door it already knows.
// See platform/learnLink.ts.
try { rewriteLearnLink() } catch { /* no window */ }

// An affiliate link, /r/<CODE>, is the short spelling of /?ref=<CODE>; then the FIRST touch
// on this device — an affiliate code or a paid ad — is remembered for the account to claim
// once there is one (job #433). See platform/affiliateTouch.ts.
try { rewriteAffiliateLink(); captureAffiliateTouch() } catch { /* no window */ }

// A tab that came by a minted /go/ link or a friend's in-game share (job #703): its tags go to
// sessionStorage and come off the address, before the router can drop the query. utm_* stay for
// the campaign capture (#702). See composables/useGoLink.ts for exactly what is kept, and where.
try { if (!isEmbedContext()) captureGoLanding() } catch { /* no window */ }

// A deep link to a course sets the interface language to that course's KNOWN
// language — the one the visitor already speaks — unless they have chosen a
// language for themselves. Runs here, before createApp, so the first paint is
// already right rather than flashing English and repainting.
// See utils/deepLinkLocale.ts for the inference-never-overrides rule.
// The framed demo infers its language the same way but stores nothing —
// see platform/embedMode.ts and the `ephemeral` flag in deepLinkLocale.
if (typeof location !== 'undefined') {
  applyBootLocale(location.search, { ephemeral: isEmbedContext(), pathname: location.pathname })
}

// Cold-start boot marks (all from navigation start via performance.now()).
// mainExec = the main bundle (Vue + App + its static dep tree) has finished
// parsing/evaluating and the entry body is running; nav→mainExec is HTML +
// main-bundle fetch+parse. mounted = + createApp/mount. The player chunk +
// LearningPlayer.onMounted happen after; the [ColdStart] event reads these
// back to decompose the app-shell prefix.
const __ssiBoot = (window.__ssiBoot = window.__ssiBoot || {})
__ssiBoot.mainExecMs = Math.round(typeof performance !== 'undefined' ? performance.now() : 0)

// Web fonts, off the critical path. This module script is deferred and the
// links it appends are media="print" until they load, so a hanging font host
// can no longer stop the app painting — it just renders in the system fallback
// every font token already names. See utils/loadWebFonts.ts.
// An account holder's open (index.html's early boot is in flight) waits for the
// first sound, capped at 6 s (job #298; Tom, 2026-10-16: never more than 3 s,
// slow 4G included): ~120 KB of faces shared a ~200 KB/s pipe with the first
// round's clips and the catalogue the player mounts on. A brand-new visitor's
// typed welcome keeps its fonts at once.
if (window.__ssiEarlyBoot) {
  let fontsAsked = false
  const askFonts = () => { if (!fontsAsked) { fontsAsked = true; loadWebFonts() } }
  void firstSound().then(askFonts)
  setTimeout(askFonts, 6000)
} else {
  loadWebFonts()
}

// Debug tooling — preview deploys (*.vercel.app) or ?debug only, NEVER
// production. eruda is an on-screen console/network inspector so logs are
// visible ON the iPhone without tethering to a Mac.
//
// BUNDLED, not CDN (Tom 2026-05-28): the CDN script can't load in airplane
// mode, so the console was dead during exactly the offline-playback tests it
// was needed for. A dynamic import gets code-split into its own chunk that the
// SW precaches (globPatterns **/*.js) — so once the app has been opened online
// once, the console works fully offline. Loaded only when the debug gate is on.
// NEVER in the framed demo. eruda is a developer's on-screen console; a
// marketing visitor on a landing page is not a developer, and it writes six
// localStorage keys of its own on an origin the demo promises to leave clean.
const DEBUG_TOOLS =
  typeof location !== 'undefined' &&
  !isEmbedContext() &&
  (location.search.includes('debug') ||
    // Not on the word game's previews (job #406): they are handed to players, and eruda
    // watching every DOM change froze answers for seconds at a time. ?debug still opens it.
    (location.hostname.includes('vercel.app') && !location.pathname.startsWith('/words')))
if (DEBUG_TOOLS && !window.eruda) {
  import('eruda').then(({ default: eruda }) => {
    try {
      eruda.init()
      // Park the floating button bottom-left — the player's transport + mode
      // controls live across the top and centre; eruda's default spot overlaps
      // them. Still draggable.
      eruda.position({ x: 6, y: window.innerHeight - 52 })
      // Panel defaults to full-height and opaque, which hides the player during
      // live play. Shrink to the bottom ~45% and make it semi-transparent so
      // logs scroll underneath while the player stays visible. Tunable in
      // eruda's own Settings tab.
      const settings = eruda.get('settings')
      if (settings) {
        settings.set('displaySize', 45)
        settings.set('transparency', 0.85)
      }
    } catch (e) { /* noop */ }
  }).catch(() => { /* chunk not precached yet (first load was offline) — noop */ })
}

// `?wedge=1` dev cheat (docs/pwa-lifecycle-design.md §3, T6/T7): deliberately
// corrupts two precached JS chunks so a tester can rehearse the boot
// watchdog's recovery without waiting for a real browser-update wedge.
// __ENABLE_WEDGE_CHEAT__ is false on staging/production builds. Guarded by
// sessionStorage so it poisons exactly once per session — the poisoned
// reload is meant to fail and hand off to the watchdog's own heal ladder,
// not re-poison itself on every subsequent heal reload.
// @ts-ignore - __ENABLE_WEDGE_CHEAT__ is defined by Vite
const ENABLE_WEDGE_CHEAT = typeof __ENABLE_WEDGE_CHEAT__ !== 'undefined' && __ENABLE_WEDGE_CHEAT__
if (ENABLE_WEDGE_CHEAT && location.search.includes('wedge=1') && 'caches' in window) {
  const ARMED_KEY = 'ssi-wedge-armed'
  let alreadyArmed = false
  try { alreadyArmed = sessionStorage.getItem(ARMED_KEY) === '1' } catch { /* storage blocked */ }
  if (!alreadyArmed) {
    try { sessionStorage.setItem(ARMED_KEY, '1') } catch { /* storage blocked */ }
    poisonPrecacheForWedgeTest().catch(() => {}).finally(() => window.location.reload())
  }
}

async function poisonPrecacheForWedgeTest() {
  const entryGraphUrls = Array.from(
    document.querySelectorAll('script[src], link[rel="modulepreload"][href]')
  ).map((el) => el.src || el.href)

  const entries = [] // { cache, request }
  for (const cacheName of await caches.keys()) {
    const cache = await caches.open(cacheName)
    for (const request of await cache.keys()) {
      if (request.url.endsWith('.js')) entries.push({ cache, request })
    }
  }

  const targets = new Set(
    selectPrecacheEntriesToPoison(entries.map((e) => e.request.url), entryGraphUrls)
  )

  await Promise.all(
    entries
      .filter((e) => targets.has(e.request.url))
      .map((e) =>
        e.cache.put(
          e.request,
          new Response('/* ssi wedge poison test — deliberately corrupt */', {
            headers: { 'Content-Type': 'application/javascript' },
          })
        )
      )
  )
}

const app = createApp(App)

// Configure router
app.use(router)
// The router restores the address it first read, our /go/ and share parameters included (job #703).
router.isReady().then(() => stripGoParams(router)).catch(() => {})

app.mount('#app')
__ssiBoot.mountedMs = Math.round(typeof performance !== 'undefined' ? performance.now() : 0)

// Boot handshake for the inline watchdog in index.html — it can't import
// anything (must run even when every module fetch fails), so this is the
// one signal it polls for. Set immediately on mount, before anything else
// that could throw. See utils/bootHeal.ts + docs/pwa-lifecycle-design.md §2.1.
window.__SSI_BOOTED = true

// Remove loading state once app is mounted
clearBootClasses(document.getElementById('app'))

// vite:preloadError fires when a dynamic import 404s because a deploy
// rotated hashed chunk filenames mid-session (the chunk the running page
// knows about no longer exists). Reload once to pick up the new build;
// sessionStorage-guarded so a genuinely broken chunk doesn't reload-loop.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  // Admin pages never reload themselves: the update banner offers the restart.
  if (inNoReloadZone()) return
  const GUARD_KEY = 'ssi-preload-error-reloaded'
  let alreadyReloaded = false
  try { alreadyReloaded = sessionStorage.getItem(GUARD_KEY) === '1' } catch { /* storage blocked */ }
  if (!shouldReloadForPreloadError(alreadyReloaded)) return
  try { sessionStorage.setItem(GUARD_KEY, '1') } catch { /* storage blocked */ }
  window.location.reload()
})
